<template>
  <div class="ag-filter__wrapper" :data-qa="normalizeQa(dataQaPrefix ? `${dataQaPrefix}__container` : undefined)">
    <div :id="id" class="ag-filter">
      <div v-if="hasContent" class="ag-filter__content">
        <div v-if="$slots['prepend-title']" class="ag-filter__prepend-title">
          <slot name="prepend-title" />
        </div>

        <div v-if="!hideTitle" class="ag-filter__title">
          <slot name="title">
            <p class="ag-filter__title-text" v-html="filterTitle" />
          </slot>
        </div>

        <div v-if="$slots['append-title'] || defaultNotEncapsulated" class="ag-filter__append-title">
          <slot name="append-title" />
          <slot v-if="!encapsulated" name="default" />
        </div>
      </div>

      <div class="ag-filter__actions">
        <AGButton
          v-if="!hideToggleButton && encapsulated"
          :id="`${id}-toggle-button`"
          icon-size="22px"
          left-icon="md:filter_alt"
          type="secondary"
          :disabled="disableToggle"
          :data-qa="normalizeQa(dataQaPrefix ? `${dataQaPrefix}__btn-toggle` : undefined)"
          @click="menu = !menu"
        >
          {{ filterToggleButtonLabel }}
        </AGButton>

        <AGButton
          v-if="!encapsulated"
          :id="`${id}-apply-button`"
          :disabled="disableApply"
          :data-qa="normalizeQa(dataQaPrefix ? `${dataQaPrefix}__btn-apply` : undefined)"
          @click="emitClickApply"
        >
          {{ applyButtonText }}
        </AGButton>

        <AGButton
          v-if="!encapsulated"
          :id="`${id}-clear-button`"
          icon-size="22px"
          left-icon="mdi-delete-outline"
          type="tertiary"
          :disabled="disableClear"
          :data-qa="normalizeQa(dataQaPrefix ? `${dataQaPrefix}__btn-clear` : undefined)"
          @click="emitClickClear"
        >
          {{ clearButtonText }}
        </AGButton>

        <slot name="export">
          <AGButton
            v-if="hasExportButton"
            :id="`${id}-export-button`"
            icon-size="22px"
            left-icon="mdi-download-outline"
            type="secondary"
            :disabled="disableExport"
            :loading="loadingExport"
            @click="emitClickExport"
          >
            {{ exportButtonText }}
            <slot name="append-inner" />
          </AGButton>
        </slot>
      </div>
    </div>

    <AGFilterMenu
      v-model="menu"
      :target="`#${id}-toggle-button`"
      :disable-apply="disableApply"
      :disable-clear="disableClear"
      @click:apply="emitClickApply"
      @click:clear="emitClickClear"
      @click:close="emitClickClose"
      @open="emitClickOpen"
    >
      <template #prepend>
        <slot name="menu-prepend" />
      </template>

      <slot name="default" />
    </AGFilterMenu>

    <div v-if="$slots.tags" class="ag-filter__tags">
      <slot name="tags" />
    </div>
  </div>
</template>

<script>
import AGButton from '@/components/common/AGButton.vue';
import AGFilterMenu from '@/components/common/AGFilterMenu.vue';
import UtilsString from '@/utils/UtilsString';

export default {
  components: {
    AGButton,
    AGFilterMenu,
  },

  emits: ['click:apply', 'click:clear', 'click:close', 'click:export', 'click:open'],

  props: {
    filterTitle: {
      type: String,
      default: '<strong>Filtro</strong>',
    },

    applied: {
      type: [String, Number],
      default: 0,
    },

    id: {
      type: String,
      default: 'ag-filter',
    },

    encapsulated: {
      type: Boolean,
      default: false,
    },

    hideTitle: {
      type: Boolean,
      default: false,
    },

    closeOnApply: {
      type: Boolean,
      default: false,
    },

    closeOnClear: {
      type: Boolean,
      default: false,
    },

    disableApply: {
      type: Boolean,
      default: false,
    },

    disableClear: {
      type: Boolean,
      default: false,
    },

    disableExport: {
      type: Boolean,
      default: false,
    },

    disableToggle: {
      type: Boolean,
      default: false,
    },

    hasExportButton: {
      type: Boolean,
      default: false,
    },

    toggleButtonText: {
      type: String,
      default: 'Filtro',
    },

    applyButtonText: {
      type: String,
      default: 'Aplicar filtro',
    },

    clearButtonText: {
      type: String,
      default: 'Limpar filtro',
    },

    exportButtonText: {
      type: String,
      default: 'Exportar',
    },

    filterQuantityText: {
      type: [String, Number],
      default: 0,
    },

    hideToggleButton: {
      type: Boolean,
      default: false,
    },

    loadingExport: {
      type: Boolean,
      default: false,
    },
    dataQaPrefix: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      menu: false,
    };
  },

  computed: {
    filterToggleButtonLabel() {
      let label = this.toggleButtonText;

      if (this.filterQuantityText) label += ` (${this.filterQuantityText})`;

      return label;
    },
    defaultNotEncapsulated() {
      return this.$slots.default && !this.encapsulated;
    },
    hasContent() {
      return (
        this.$slots['prepend-title'] || this.$slots['append-title'] || !this.hideTitle || this.defaultNotEncapsulated
      );
    },
  },

  methods: {
    normalizeQa: UtilsString.normalizeQa,
    emitClickApply() {
      this.$emit('click:apply');
    },

    emitClickClear() {
      this.$emit('click:clear');
    },

    emitClickClose() {
      this.$emit('click:close');
    },

    emitClickExport() {
      this.$emit('click:export');
    },

    emitClickOpen() {
      this.$emit('click:open');
    },

    fecharDropdown() {
      this.menu = false;
    },
  },
};
</script>

<style scoped lang="scss">
.ag-filter__wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  gap: 16px;
}

.ag-filter {
  flex: 1 0 auto;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  gap: 16px;

  .ag-filter__content {
    flex: 1 1 auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    .ag-filter__prepend-title {
      flex: 1 1 auto;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
    }

    .ag-filter__title {
      flex: 1 0 auto;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;

      .ag-filter__title-text {
        margin: 0;
        color: $neutral-color-high-dark;
        font-size: $font-size-sm;
      }
    }

    .ag-filter__append-title {
      flex: 1 0 auto;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
      gap: 16px;
    }
  }

  .ag-filter__actions {
    flex: 0 1 auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
  }
}
</style>
